import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const WasteHunter = () => {
  const navigate = useNavigate();
  const [fixedItems, setFixedItems] = useState(new Set());

  // Mock waste detection data
  const wasteItems = [
    {
      id: 'leak_001',
      type: 'paused_spending',
      title: 'Paused Campaign Still Spending',
      description: 'Black Friday Sale campaign shows "Paused" but spent $47 yesterday',
      impact: '$47/day',
      totalWaste: '$1,410',
      severity: 'critical',
      platform: 'Facebook',
      fixTime: '30 seconds',
      autoFix: true
    },
    {
      id: 'leak_002',
      type: 'audience_overlap',
      title: 'Massive Audience Overlap',
      description: '73% overlap between Holiday Collection and Winter Sale campaigns',
      impact: '$89/day',
      totalWaste: '$2,670',
      severity: 'high',
      platform: 'Google Ads',
      fixTime: '2 minutes',
      autoFix: false
    },
    {
      id: 'leak_003',
      type: 'unused_tools',
      title: 'Unused Marketing Tools',
      description: 'Paying for 5 tools nobody has logged into for 30+ days',
      impact: '$297/month',
      totalWaste: '$3,564',
      severity: 'medium',
      platform: 'Subscriptions',
      fixTime: '5 minutes',
      autoFix: false
    },
    {
      id: 'leak_004',
      type: 'keyword_waste',
      title: 'Irrelevant Keywords Burning Cash',
      description: '47 keywords with 0% conversion rate still active, eating budget',
      impact: '$23/day',
      totalWaste: '$690',
      severity: 'medium',
      platform: 'Google Ads',
      fixTime: '1 minute',
      autoFix: true
    },
    {
      id: 'leak_005',
      type: 'geo_waste',
      title: 'Geographic Money Pit',
      description: 'Spending in locations with 0 conversions for 60+ days',
      impact: '$34/day',
      totalWaste: '$2,040',
      severity: 'high',
      platform: 'Facebook',
      fixTime: '45 seconds',
      autoFix: true
    },
    {
      id: 'leak_006',
      type: 'dayparting_waste',
      title: 'Night Shift Budget Burn',
      description: 'Ads running 2-6 AM with 0.1% CTR but same bid as peak hours',
      impact: '$67/day',
      totalWaste: '$2,010',
      severity: 'high',
      platform: 'Google Ads',
      fixTime: '1 minute',
      autoFix: true
    }
  ];

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return '#ff4444';
      case 'high': return '#ff8800';
      case 'medium': return '#ffaa00';
      default: return '#888';
    }
  };

  const getSeverityIcon = (severity) => {
    switch(severity) {
      case 'critical': return '🚨';
      case 'high': return '⚠️';
      case 'medium': return '⚡';
      default: return '💡';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'paused_spending': return '🩹';
      case 'audience_overlap': return '🎯';
      case 'unused_tools': return '🔧';
      case 'keyword_waste': return '🔍';
      case 'geo_waste': return '🌍';
      case 'dayparting_waste': return '🌙';
      default: return '💸';
    }
  };

  const handleQuickFix = (itemId) => {
    setFixedItems(prev => new Set(prev).add(itemId));
    // In real app, this would make API call to fix the issue
  };

  const totalDailyWaste = wasteItems
    .filter(item => !fixedItems.has(item.id))
    .reduce((sum, item) => {
      const daily = parseFloat(item.impact.replace(/[$\/day\/month]/g, ''));
      return sum + (item.impact.includes('month') ? daily / 30 : daily);
    }, 0);

  const totalMonthlyWaste = totalDailyWaste * 30;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => navigate('/')} style={styles.backButton}>
          ← Back to Dashboard
        </button>
        <h1>💸 Waste Hunter</h1>
        <p>Find and fix budget leaks draining your marketing spend</p>
        
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>${Math.round(totalDailyWaste)}</div>
            <div style={styles.statLabel}>Daily Waste</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>${Math.round(totalMonthlyWaste).toLocaleString()}</div>
            <div style={styles.statLabel}>Monthly Waste</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{wasteItems.filter(item => !fixedItems.has(item.id)).length}</div>
            <div style={styles.statLabel}>Active Leaks</div>
          </div>
        </div>
      </div>

      <div style={styles.wasteGrid}>
        {wasteItems.map(item => (
          <div 
            key={item.id} 
            style={{
              ...styles.wasteCard,
              borderColor: fixedItems.has(item.id) ? '#4ade80' : getSeverityColor(item.severity),
              opacity: fixedItems.has(item.id) ? 0.6 : 1
            }}
          >
            <div style={styles.cardHeader}>
              <div style={styles.typeInfo}>
                <span style={styles.typeIcon}>{getTypeIcon(item.type)}</span>
                <div>
                  <h3 style={styles.wasteTitle}>{item.title}</h3>
                  <span style={styles.platform}>{item.platform}</span>
                </div>
              </div>
              <div style={styles.severityBadge}>
                <span style={styles.severityIcon}>{getSeverityIcon(item.severity)}</span>
                <span style={{
                  ...styles.severity,
                  color: getSeverityColor(item.severity)
                }}>
                  {item.severity.toUpperCase()}
                </span>
              </div>
            </div>

            <div style={styles.description}>
              {item.description}
            </div>

            <div style={styles.impactSection}>
              <div style={styles.impact}>
                <strong>Bleeding:</strong> <span style={{color: '#ff4444'}}>{item.impact}</span>
              </div>
              <div style={styles.totalWaste}>
                <strong>Total Waste:</strong> <span style={{color: '#ff6666'}}>{item.totalWaste}</span>
              </div>
            </div>

            <div style={styles.cardFooter}>
              <span style={styles.fixTime}>
                ⏱️ Fix Time: {item.fixTime}
              </span>
              
              {fixedItems.has(item.id) ? (
                <div style={styles.fixedButton}>
                  ✅ Fixed!
                </div>
              ) : (
                <button 
                  style={{
                    ...styles.fixButton,
                    background: item.autoFix ? 
                      'linear-gradient(45deg, #4ade80, #22c55e)' : 
                      'linear-gradient(45deg, #ffa726, #ff9800)'
                  }}
                  onClick={() => handleQuickFix(item.id)}
                >
                  {item.autoFix ? '🚀 Auto-Fix' : '🔧 Manual Fix'}
                </button>
              )}
            </div>

            {fixedItems.has(item.id) && (
              <div style={styles.fixedOverlay}>
                <div style={styles.fixedMessage}>
                  💰 Saving {item.impact} - Issue Resolved!
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {fixedItems.size > 0 && (
        <div style={styles.summaryCard}>
          <h3>🎉 Money Saved Today</h3>
          <div style={styles.savingsAmount}>
            ${Math.round(wasteItems
              .filter(item => fixedItems.has(item.id))
              .reduce((sum, item) => {
                const daily = parseFloat(item.impact.replace(/[$\/day\/month]/g, ''));
                return sum + (item.impact.includes('month') ? daily / 30 : daily);
              }, 0)
            )}/day
          </div>
          <p>Keep hunting for more leaks!</p>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  backButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    marginTop: '2rem',
  },
  statCard: {
    background: 'rgba(255, 68, 68, 0.1)',
    border: '1px solid rgba(255, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '1rem',
    textAlign: 'center',
  },
  statNumber: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    color: '#ff4444',
  },
  statLabel: {
    fontSize: '0.9rem',
    color: '#ccc',
    marginTop: '0.5rem',
  },
  wasteGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '1.5rem',
    marginTop: '2rem',
  },
  wasteCard: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '2px solid',
    borderRadius: '12px',
    padding: '1.5rem',
    position: 'relative',
    transition: 'all 0.3s ease',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
  },
  typeInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  typeIcon: {
    fontSize: '1.5rem',
  },
  wasteTitle: {
    margin: 0,
    fontSize: '1.1rem',
  },
  platform: {
    fontSize: '0.8rem',
    color: '#888',
  },
  severityBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
  },
  severityIcon: {
    fontSize: '1rem',
  },
  severity: {
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  description: {
    color: '#ccc',
    fontSize: '0.9rem',
    marginBottom: '1rem',
    lineHeight: '1.4',
  },
  impactSection: {
    background: 'rgba(255, 68, 68, 0.1)',
    padding: '0.75rem',
    borderRadius: '6px',
    marginBottom: '1rem',
  },
  impact: {
    fontSize: '0.9rem',
    marginBottom: '0.3rem',
  },
  totalWaste: {
    fontSize: '0.9rem',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  fixTime: {
    fontSize: '0.8rem',
    color: '#888',
  },
  fixButton: {
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 'bold',
  },
  fixedButton: {
    background: '#4ade80',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: 'bold',
  },
  fixedOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(74, 222, 128, 0.1)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fixedMessage: {
    background: '#4ade80',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    fontWeight: 'bold',
  },
  summaryCard: {
    background: 'rgba(74, 222, 128, 0.1)',
    border: '2px solid #4ade80',
    borderRadius: '12px',
    padding: '2rem',
    textAlign: 'center',
    marginTop: '2rem',
  },
  savingsAmount: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#4ade80',
    margin: '0.5rem 0',
  },
};

export default WasteHunter;